import { AlertTriangle, MessageCircle } from "lucide-react";
import { useNavigate } from "react-router";

import { EmptyState } from "@/components/ui/empty-state";
import { PUBLIC_ROUTES, PROTECTED_ROUTES } from "@/constants/routes";
import {
  ConversationListItem,
  ConversationListItemSkeleton,
} from "@/features/messaging/components/ConversationListItem";
import { useConversationListRealtime } from "@/features/messaging/hooks/useConversationListRealtime";
import { useConversations } from "@/features/messaging/hooks/useConversations";
import { ApiError } from "@/types/api";

const SKELETON_COUNT = 5;

interface ChatListPageProps {
  /** The `:conversationId` param from the sibling detail route, threaded
   * down by `ChatPage` (docs/ui/routes.md's split-view) so the row for the
   * open thread gets `ConversationListItem`'s `isActive` highlight —
   * `ChatListPage` never reads the URL itself, since it renders identically
   * whether it's the mobile full-width list or the desktop split-view's
   * left pane. */
  selectedConversationId?: string;
}

/**
 * docs/ui/routes.md `/chat` — Chat / Messages (list). Rendered by `ChatPage`
 * as the split-view's left pane (desktop) or the whole screen (mobile, no
 * conversation open yet) — see ChatPage.tsx for how the two panes combine
 * into the synced two-pane layout the doc calls for.
 */
export function ChatListPage({ selectedConversationId }: ChatListPageProps) {
  const navigate = useNavigate();
  const conversationsQuery = useConversations();
  useConversationListRealtime();

  return (
    <div className="flex h-full flex-col">
      <h1 className="shrink-0 border-b border-border px-4 py-4 text-h3 text-foreground">
        Messages
      </h1>

      <div className="flex-1 overflow-y-auto">
        {conversationsQuery.isPending &&
          Array.from({ length: SKELETON_COUNT }, (_, index) => (
            <ConversationListItemSkeleton key={index} />
          ))}

        {conversationsQuery.isError && (
          <div className="p-6">
            <EmptyState
              icon={AlertTriangle}
              heading="Couldn't load your messages"
              description={
                conversationsQuery.error instanceof ApiError
                  ? conversationsQuery.error.message
                  : "Something went wrong. Please try again."
              }
              action={{ label: "Retry", onClick: () => void conversationsQuery.refetch() }}
            />
          </div>
        )}

        {conversationsQuery.data?.length === 0 && (
          <div className="p-6">
            <EmptyState
              icon={MessageCircle}
              heading="No messages yet"
              description="When you contact a seller or someone messages you about a listing, it'll show up here."
              action={{ label: "Explore Products", onClick: () => navigate(PUBLIC_ROUTES.home) }}
            />
          </div>
        )}

        {conversationsQuery.data?.map((conversation) => (
          <ConversationListItem
            key={conversation.id}
            conversation={conversation}
            isActive={conversation.id === selectedConversationId}
            onClick={() =>
              navigate(PROTECTED_ROUTES.chatConversation.replace(":conversationId", conversation.id))
            }
          />
        ))}
      </div>
    </div>
  );
}
